import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { GoogleApiService } from 'app/services/google-api.service';
import { catchError, forkJoin, map, of, switchMap, zip } from 'rxjs';
import { BookingService } from '../services/booking.service';
import { PlacesService } from '../services/places.service';
import * as SearchLocationsActions from '../store/searchLocations.actions';
import {
    selectCurrentMapCoordinates,
    selectSearchLocationsFilters,
    selectSearchLocationsResults,
} from './searchLocations.selectors';

@Injectable()
export class SearchLocationsEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private _bookingService = inject(BookingService);
    private _placesService = inject(PlacesService);
    private _googleApiService = inject(GoogleApiService);

    coordinates$ = this._store.select(selectCurrentMapCoordinates);
    filters$ = this._store.select(selectSearchLocationsFilters);
    places$ = this._store.select(selectSearchLocationsResults);

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // -----------------------------------------------------------
    // @ Search Locations Effects
    // -----------------------------------------------------------

    triggerStartSearchLocations$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SearchLocationsActions.setSearchFilters),
            map(() => SearchLocationsActions.startSearchLocations())
        )
    );

    searchLocations$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SearchLocationsActions.startSearchLocations),

            // Subscribe to know current coordinates and filters (just the places types)
            switchMap(() => {
                return zip([this.coordinates$, this.filters$]);
            }),

            // Make a request of places API for each location type
            switchMap(([coordinates, filters]) => {
                if (
                    !coordinates.latitude ||
                    !coordinates.longitude ||
                    !filters.state ||
                    !filters.city ||
                    filters.locationTypes.length === 0
                ) {
                    //   return of(
                    //     SearchLocationsActions.startSearchLocationsError({
                    //       error: 'No coordinates or filters available',
                    //     })
                    //   );
                    console.log(
                        'No coordinates or filters available for search locations'
                    );
                    return of([]);
                }
                return forkJoin(
                    filters.locationTypes.map((locationType: string) =>
                        this._googleApiService.placesApiTextSearch(
                            {
                                lat: coordinates.latitude,
                                lng: coordinates.longitude,
                            },
                            locationType
                        )
                    )
                );
            }),

            // Debug
            // tap((res) => {
            //     res.forEach((search) => {
            //         console.log('Search Locations Response:', search);
            //     });
            // }),

            // Trigger Success
            map((results) =>
                SearchLocationsActions.startSearchLocationsSuccess({
                    results: results.flat(),
                })
            ),
            // Handle error
            catchError((error) =>
                of(
                    SearchLocationsActions.startSearchLocationsError({
                        error: error?.message || 'Unknown error',
                    })
                )
            )
        )
    );

    placesToMarkers$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SearchLocationsActions.startSearchLocationsSuccess),
            switchMap(() => this.places$),
            map((places) =>
                SearchLocationsActions.setMarkers({
                    markers: this._placesService.placesToMarkers(places),
                })
            )
        )
    );
}
